import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import type { MapareRow, NomenclatorEntry } from '@lyra/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { fieldAria } from '@/lib/formA11y';
import { buildEditPayload } from './lib/classify';
import { useNomenclatorSearch, useResolveEntry } from './api/reviewHooks';

const NOMENCLATOR_DEBOUNCE_MS = 300;

const sectionLabelCls = 'mb-1 text-[11.5px] font-semibold uppercase tracking-wide text-ink-3';

/**
 * The orchestrator's own fixed English boilerplate for an auto-resolved
 * row leaks verbatim into this otherwise-Romanian panel (2026-08-19
 * remediation copy audit — „MOTIVAȚIE: Resolved by the model from the
 * candidate set."). This is a known FIXED string the pipeline emits, not
 * free-text reasoning, so it can be translated exactly like `format.ts`'s
 * enum-label maps; anything else in `reasoning` is genuine classifier
 * free-text and is shown verbatim.
 */
const REASONING_TRANSLATIONS: Record<string, string> = {
  'Resolved by the model from the candidate set.': 'Rezolvat de model din setul de candidați.',
};
function displayReasoning(reasoning: string): string {
  return REASONING_TRANSLATIONS[reasoning] ?? reasoning;
}

/** Free-text field validators — ported from v1 `utils/index.ts`
 *  (validateCodMedoc / validateDenumire), the manual-edit escape hatch v2
 *  dropped. Returns an error string, or null when valid. Exported (Plan
 *  3c-2's own "Editează manual" section, plus T4's `WizardListStep` inline
 *  row editor, both need the exact same rules — single source of truth). */
export function validateCodMedoc(cod: string): string | null {
  const t = cod.trim();
  if (!t) return 'Codul nu poate fi gol';
  if (!/^[A-Za-z0-9.]+$/.test(t)) return 'Codul poate conține doar litere, cifre și puncte';
  if (t.length > 20) return 'Codul trebuie să aibă maximum 20 de caractere';
  return null;
}
export function validateDenumire(den: string): string | null {
  const t = den.trim();
  if (!t) return 'Denumirea nu poate fi goală';
  if (t.length > 255) return 'Denumirea trebuie să aibă maximum 255 de caractere';
  return null;
}

export interface CorrectionPanelProps {
  row: MapareRow;
  runId: number;
  /** Called after any mutation succeeds (e.g. so the host row can collapse itself). */
  onDone?: () => void;
  /**
   * Accessibility remediation (2026-08-19 Slice B, Task 2): the `id`
   * `ResultsTable`'s `RowExpandToggle` points its `aria-controls` at — set
   * on the panel's own root so the two agree, and Escape (below) closes
   * back to it via `onRequestClose`.
   */
  id?: string;
  /**
   * Escape inside the panel — distinct from `onDone` (a successful
   * mutation, which also collapses the row but has no single sensible
   * focus target since the row is about to re-bucket). `onRequestClose`
   * collapses AND returns focus to the toggle that opened this panel, so
   * an operator who backs out with Escape lands exactly where they
   * started rather than on `<body>`.
   */
  onRequestClose?: () => void;
  /** Task 6 (Plan 3c-3): true while the row's run is finalized — the server 409s entry
   *  edits on a finalized run (T2), so every mutating control here (Confirmă, Alege
   *  această variantă, nomenclator search, Fără corespondent) is disabled and no
   *  mutation is ever fired. The reasoning + candidate DISPLAY stays visible. */
  readOnly?: boolean;
  /**
   * How the same controls are arranged (2026-08-21 UI report).
   *
   * - `'stacked'` (default) — `ResultsTable`'s expandable disclosure: one
   *   column, motivație first, terminal decisions last. Unchanged.
   * - `'review'` — the wizard's „Revizuire"/„Încredere scăzută" steps: the
   *   decision bar leads, partner and model sit in two LABELLED columns, and
   *   everything secondary (alternates, model comparison, nomenclator search,
   *   manual edit) folds into a closed disclosure. Fixes all three reported
   *   defects at once: unclear which denumire came from where, having to
   *   scroll a tall panel to mark a row incorect/fără corespondent, and the
   *   duplicate „Confirmă"/„Aplică" pair.
   */
  layout?: 'stacked' | 'review';
}

/**
 * Model votes worth showing as a SECOND opinion: drops any vote that repeats
 * the primary suggestion (that pair was the reported „2 butoane care fac
 * aparent acelasi lucru"), any vote that repeats an earlier vote, and any
 * vote with no code at all (nothing to apply). Compared case-insensitively
 * and whitespace-trimmed, since codes arrive from two different models.
 */
export function distinctVotes<T extends { cod_medoc?: string }>(votes: T[], primaryCodMedoc?: string): T[] {
  const seen = new Set<string>();
  const primary = (primaryCodMedoc ?? '').trim().toLowerCase();
  if (primary) seen.add(primary);
  return votes.filter((v) => {
    const key = (v.cod_medoc ?? '').trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Shared focus-disclosure contract (2026-08-19 Slice B, Task 2; extracted
 * for the fix round's item 2). Focus-in on mount, Escape-to-request-close,
 * Tab trapped between the panel's first and last ENABLED focusable control
 * — `panelRef` goes on the panel's root; `headingRef` on a visually-hidden
 * `<h3 tabIndex={-1}>` inside it (the focus-in target: the operator's very
 * next Tab starts inside the decision, not two rows below it, and a screen
 * reader announces which row is under review the moment focus lands).
 *
 * Originally built inline for `CorrectionPanel` alone. Code review found
 * `ResultsTable`'s OTHER `DetailItem` branch — the „Rezolvate" plain-
 * reasoning view — shares the exact same toggle, `panelId`, and
 * `aria-controls` target as this component, but had none of this contract:
 * the identical arrow behaved differently depending on which branch
 * happened to render. Extracting the logic here (rather than copying it)
 * means both branches share one implementation that can't drift apart —
 * `ResultsTable.tsx`'s `DetailItem` imports this same hook for its plain-
 * reasoning branch.
 *
 * Gated on `onRequestClose` being provided at all — `ResultsTable` always
 * passes it (this is its disclosure-panel use, one row expanded at a time
 * behind a toggle: exactly what focus-in/Escape/Tab-trap are for). The
 * OTHER caller of `CorrectionPanel`, `WizardReviewStep`, renders every
 * row's panel inline and simultaneously, always visible, no toggle, no
 * `onRequestClose` — a flat editorial list, not a disclosure. Without this
 * gate, mounting that list would steal focus onto the LAST row's hidden
 * heading (every panel's mount-effect firing in order, each stealing focus
 * from the last) and trap Tab inside whichever row happened to have focus,
 * breaking normal page-flow navigation between rows — a real regression
 * that caller never asked for.
 */
export function useDisclosureFocusTrap(onRequestClose?: () => void) {
  const panelRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const active = onRequestClose != null;

  useEffect(() => {
    if (!active) return;
    headingRef.current?.focus();
    // Mount-only, deliberately: the component calling this hook is
    // recreated per row expansion (`ResultsTable`'s `DetailItem` swaps the
    // whole subtree on a different `key`), so there is no dependency to
    // track here — a change of row already means a fresh mount, which
    // already re-runs this effect. (No `react-hooks/exhaustive-deps`
    // directive here: this repo's `eslint.config.js` does not register that
    // plugin, so an eslint-disable comment for it would itself be dead code
    // — see task-1-report.md.)
  }, []);

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (!active) return;
    if (e.key === 'Escape') {
      // The nomenclator combobox's own `handleSearchKeyDown` (below) already
      // handles Escape for ITS narrower scope (clear the search + close the
      // dropdown) and calls `preventDefault()` when it does — respect that:
      // don't also close the whole panel on the same keypress.
      if (e.defaultPrevented) return;
      e.preventDefault();
      e.stopPropagation();
      onRequestClose?.();
      return;
    }
    if (e.key !== 'Tab') return;
    const focusable = panelRef.current?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    // Focus rests on `headingRef` (tabIndex=-1) right after opening — it's
    // deliberately outside `focusable` above (it's not part of the normal
    // tab sequence), so it needs its own check here: Shift+Tab pressed from
    // that resting point must still wrap to `last`, not escape the panel
    // via whatever the browser's default "previous focusable" happens to be
    // (the toggle button, which precedes the panel in the DOM).
    if (e.shiftKey && (document.activeElement === first || document.activeElement === headingRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return { panelRef, headingRef, handleKeyDown };
}

/**
 * Direction-B inline correction panel (Plan 3c-2 Task 4) — replaces v1's
 * `EditModal.tsx` dialog and `ReviewQueueView.tsx` master-detail actions with
 * a single no-modal panel meant to render inside the results table's
 * expandable detail row (Task 5 wires the mount point). Spec §4:
 * `docs/superpowers/specs/2026-07-17-lyra-v2-plan-3c2-mapari-review-correction-design.md`.
 *
 * Top-to-bottom, hairline-separated (`border-rule-soft`), no boxed cards,
 * no dialog chrome:
 *   1. Model reasoning (the 3c-1 „Motivație" text) — carried over so the
 *      panel is self-contained even where it replaces `ResultsTable`'s
 *      previous reasoning-only detail row.
 *   2. Best-effort „Clasificare originală" amber note — only when
 *      `row.originalCodMedoc` is present. The server does NOT persist
 *      `originalCodMedoc`/`originalDenumireMedoc` (confirmed in T3's report:
 *      `updateClassification`'s whitelist has no case for either field, and
 *      `toClassificationRow` hardcodes both to `undefined` on every read) —
 *      so this note is transient: it shows only for a row that still carries
 *      the value in memory (e.g. immediately after `buildEditPayload` stashed
 *      it, before the next refetch), and silently disappears on reload. Not
 *      a bug to "fix" here — flagged as a known gap, per the T3 report.
 *   3. Primary suggestion (`codMedoc||provisionalCodMedoc` /
 *      `denumireMedoc||provisionalDenumireMedoc`) with a „Confirmă" action.
 *   4. Alternate candidates, each a selectable „Alege această variantă" line.
 *   5. A debounced (300ms) nomenclator search combobox — keyboard nav +
 *      aria wiring ported from v1 `EditModal.tsx`'s
 *      `handleSearchKeyDown`/`pickNomenclatorEntry` (Rule Zero source).
 *   6. „Fără corespondent" — marks the row unresolved and clears its codes.
 *
 * Button copy: item 3/6's quoted labels („Confirmă", „Fără corespondent")
 * are the task's literal spec text — deliberately shorter than v1
 * `ReviewQueueView.tsx`'s "Confirmă varianta principală"/"Marchează
 * nerezolvat" (Direction-B's quieter, inline idiom; compare `ResultsTable`'s
 * single-word "Revizuiește" affordance vs. v1's full sentences). Only
 * "Alege această variantă" is shared verbatim between the spec and v1 — not
 * a discrepancy, both sources agree on that one.
 */
export function CorrectionPanel({ row, runId, onDone, id, onRequestClose, readOnly = false, layout = 'stacked' }: CorrectionPanelProps) {
  const resolveEntry = useResolveEntry();
  const isPending = resolveEntry.isPending;

  // Accessibility remediation (2026-08-19 Slice B, Task 2 + fix round item
  // 2): focus-in on open, Escape-to-close, Tab trapped inside while open —
  // see `useDisclosureFocusTrap`'s own doc comment above for the full
  // contract and why it's gated on `onRequestClose`.
  const { panelRef, headingRef, handleKeyDown: handlePanelKeyDown } = useDisclosureFocusTrap(onRequestClose);

  // ── Nomenclator combobox: debounced search + full keyboard nav, ported
  //    from v1 EditModal.tsx (search debounce effect, outside-click close,
  //    handleSearchKeyDown, pickNomenclatorEntry). `term` is what the input
  //    shows immediately; `debouncedTerm` (and therefore the query) lags by
  //    300ms so keystrokes don't each fire a request.
  const [term, setTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  /** Last nomenclator entry applied from the dropdown — echoed back under the
   *  search so the (already immediate) save is visibly terminal, not something
   *  the operator still has to confirm. */
  const [applied, setApplied] = useState<{ codMedoc: string; denumireMedoc: string } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedTerm(term), NOMENCLATOR_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [term]);

  const { data: searchData, isFetching } = useNomenclatorSearch(debouncedTerm);
  const results = useMemo(() => searchData?.items ?? [], [searchData]);

  // Reopen (and reset the active option) whenever a new debounced search
  // actually resolves with matches — mirrors v1's `.then(({items}) => {
  // setOpen(items.length > 0); setActiveIndex(-1); })`.
  useEffect(() => {
    setOpen(results.length > 0 && debouncedTerm.trim().length >= 2);
    setActiveIndex(-1);
  }, [results, debouncedTerm]);

  // Outside click closes the listbox — same `document.mousedown` pattern as
  // v1 (there's no modal here to also close, just the dropdown).
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function resolve(patch: Partial<MapareRow>) {
    // Belt-and-suspenders: every control that calls `resolve` is already
    // disabled when `readOnly` (the run is finalized — the server 409s entry
    // edits, T2), but never fire the mutation from here either.
    if (readOnly) return;
    resolveEntry.mutate({ id: row.id, runId, patch }, { onSuccess: () => onDone?.() });
  }

  function pickEntry(entry: NomenclatorEntry) {
    resolve(
      buildEditPayload(row, {
        codMedoc: entry.codMedoc,
        denumireMedoc: entry.denumireMedoc,
        codBdMedoc: entry.codBdMedoc,
      })
    );
    setApplied({ codMedoc: entry.codMedoc, denumireMedoc: entry.denumireMedoc });
    setTerm('');
    setDebouncedTerm('');
    setOpen(false);
    setActiveIndex(-1);
  }

  function handleSearchKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!open || results.length === 0) {
      if (e.key === 'ArrowDown' && results.length > 0) {
        e.preventDefault();
        setOpen(true);
        setActiveIndex(0);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      pickEntry(results[activeIndex]);
    } else if (e.key === 'Escape') {
      // No modal to close here — Escape just clears the search + dropdown.
      e.preventDefault();
      setTerm('');
      setDebouncedTerm('');
      setOpen(false);
      setActiveIndex(-1);
    }
  }

  const primaryCodMedoc = row.codMedoc || row.provisionalCodMedoc;
  const primaryDenumireMedoc = row.denumireMedoc || row.provisionalDenumireMedoc;
  const listboxId = `correction-panel-listbox-${row.id}`;

  // B1 (parity — v1 EditModal free-text edit): let the operator type a code /
  // name / cod-BD directly, the escape hatch for a value the model didn't
  // propose and that isn't in the (possibly org-empty) nomenclator. Seeded from
  // the row's current values; the panel remounts per expansion so no resync needed.
  const [editCod, setEditCod] = useState(row.codMedoc ?? '');
  const [editDen, setEditDen] = useState(row.denumireMedoc ?? '');
  const [editBd, setEditBd] = useState(row.codBdMedoc ?? '');
  const codErr = editCod.trim() ? validateCodMedoc(editCod) : null;
  const denErr = editDen.trim() ? validateDenumire(editDen) : null;
  const editValid = validateCodMedoc(editCod) === null && validateDenumire(editDen) === null;


  // ── Section fragments ────────────────────────────────────────────────────
  // Extracted so the two layouts below can ORDER them differently without
  // either one re-implementing a control. `stacked` (the ResultsTable
  // disclosure) keeps the original top-to-bottom reading order; `review` (the
  // wizard's „Revizuire"/„Încredere scăzută" steps) leads with the decision
  // and folds the rest away — see the component doc comment.

  const confirmPatch: Partial<MapareRow> = row.primaryCandidate
    ? {
        reviewResolution: 'confirmed_primary',
        codMedoc: row.primaryCandidate.codMedoc,
        denumireMedoc: row.primaryCandidate.denumireMedoc,
        codBdMedoc: row.primaryCandidate.codBdMedoc,
        status: row.status === 'edited' ? 'edited' : 'classified',
      }
    : {
        reviewResolution: 'confirmed_primary',
        codMedoc: primaryCodMedoc,
        denumireMedoc: primaryDenumireMedoc,
        codBdMedoc: row.codBdMedoc,
        status: row.status === 'edited' ? 'edited' : 'classified',
      };

  const confirmButton = (
    <Button
      type="button"
      size="sm"
      onClick={() => resolve(confirmPatch)}
      disabled={isPending || readOnly || !primaryCodMedoc}
    >
      Confirmă
    </Button>
  );

  const faraCorespondentButton = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() =>
        resolve(
          // `PATCH /classifications/:id` accepts `null` to clear a column
          // (server/src/db/data.ts's `updateClassification` only skips
          // `undefined`, so `null` passes through and NULLs the SQL
          // column) — but `MapareRow.codMedoc`/`denumireMedoc` are typed
          // as plain `string`, `codBdMedoc?: string`, not nullable, so
          // `Partial<MapareRow>` doesn't model this clearing case. Spec
          // §3 is explicit that mark-unresolved sends literal `null`s;
          // cast through `unknown` for just this one patch rather than
          // widening the shared row type for a single write-only shape.
          //
          // Fix 6 (error-row escape): also set `status: 'classified'`.
          // Without it, an ERROR row's patch never overrides
          // `status: 'error'`, so the row stays bucketed under „Erori"
          // (and stays a retry candidate — `rowsNeedingRetry` selects on
          // `status === 'error'`) even after the operator explicitly
          // marked it „Fără corespondent"; the decision was silently
          // ignored.
          {
            reviewResolution: 'unresolved',
            status: 'classified',
            codMedoc: null,
            denumireMedoc: null,
            codBdMedoc: null,
          } as unknown as Partial<MapareRow>
        )
      }
      disabled={isPending || readOnly}
    >
      Fără corespondent
    </Button>
  );

  /* B2 (parity — v1 „Marchează incorect"): flag any row as wrong, sending
      it to the „Erori" bucket where the retry loop re-classifies it. */
  const marcheazaIncorectButton = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => resolve({ status: 'error', accepted: false })}
      disabled={isPending || readOnly}
    >
      Marchează incorect
    </Button>
  );

  const reasoningSection = row.reasoning?.trim() ? (
    <section>
      <p className={sectionLabelCls}>Motivație</p>
      <p className="whitespace-pre-wrap text-[13.5px] leading-relaxed text-ink-2">{displayReasoning(row.reasoning)}</p>
    </section>
  ) : null;

  const originalSection = row.originalCodMedoc ? (
    <section className="border-t border-rule-soft pt-3">
      <p className="mb-1 text-[11.5px] font-semibold uppercase tracking-wide text-warn">
        Clasificare originală
      </p>
      <p className="text-[13.5px] text-ink-2">
        <span className="font-mono text-[12.5px] text-warn">{row.originalCodMedoc}</span>
        {row.originalDenumireMedoc && <> — {row.originalDenumireMedoc}</>}
      </p>
    </section>
  ) : null;

  const alternatesSection =
    row.alternateCandidates && row.alternateCandidates.length > 0 ? (
      <section className="border-t border-rule-soft pt-3">
        <p className={sectionLabelCls}>Variante alternative</p>
        <ul className="space-y-3">
          {row.alternateCandidates.map((candidate, i) => (
            <li key={i} className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-mono text-[13.5px] text-ink">{candidate.codMedoc || '—'}</p>
                <p className="truncate text-[13.5px] text-ink-2">{candidate.denumireMedoc || 'Fără denumire'}</p>
                {candidate.codBdMedoc && (
                  <p className="font-mono text-[12.5px] text-ink-3">{candidate.codBdMedoc}</p>
                )}
                {candidate.reasoning && (
                  <p className="mt-0.5 whitespace-pre-wrap text-[12.5px] text-ink-3">{displayReasoning(candidate.reasoning)}</p>
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() =>
                  resolve({
                    reviewResolution: 'selected_alternate',
                    codMedoc: candidate.codMedoc,
                    denumireMedoc: candidate.denumireMedoc,
                    codBdMedoc: candidate.codBdMedoc,
                    // Fix 6: matches v1 + `buildEditPayload` — an
                    // operator-picked alternate is an edit, not a bare
                    // reclassification.
                    status: 'edited',
                  })
                }
                disabled={isPending || readOnly}
              >
                Alege această variantă
              </Button>
            </li>
          ))}
        </ul>
      </section>
    ) : null;

  /* B4 (parity — v1 dual-model voting comparison): the orchestrator's
      per-model votes are persisted + sent to the client but v2 never
      rendered them. Show each model's proposal + a consensus flag, each
      applyable in one click.

      2026-08-21 („aplica/confirma — de ce avem 2 butoane care fac aparent
      acelasi lucru"): a model that voted for the SAME code as the primary
      suggestion used to render its own „Aplică" right under „Confirmă" —
      two buttons, one outcome. `distinctVotes` drops those, so this
      section only ever shows a genuine SECOND opinion. */
  const otherVotes = row.voting?.enabled ? distinctVotes(row.voting.votes, primaryCodMedoc) : [];
  const votingSection =
    row.voting?.enabled && otherVotes.length > 0 ? (
      <section className="border-t border-rule-soft pt-3">
        <p className={sectionLabelCls}>
          Comparație modele
          <span className={`ml-1.5 font-normal normal-case tracking-normal ${row.voting.consensus ? 'text-ok' : 'text-warn'}`}>
            · {row.voting.consensus ? 'consens' : 'fără consens'}
          </span>
        </p>
        <ul className="space-y-3">
          {otherVotes.map((v, i) => (
            <li key={i} className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11.5px] uppercase tracking-wide text-ink-3">{v.model || `Model ${i + 1}`}</p>
                <p className="font-mono text-[13.5px] text-ink">{v.cod_medoc || '—'}</p>
                <p className="truncate text-[13.5px] text-ink-2">{v.denumire_medoc || 'Fără denumire'}</p>
                {v.cod_bd_medoc && <p className="font-mono text-[12.5px] text-ink-3">{v.cod_bd_medoc}</p>}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                disabled={isPending || readOnly || !v.cod_medoc}
                onClick={() =>
                  resolve({
                    reviewResolution: 'selected_alternate',
                    codMedoc: v.cod_medoc,
                    denumireMedoc: v.denumire_medoc,
                    codBdMedoc: v.cod_bd_medoc,
                    status: 'edited',
                  })
                }
              >
                Aplică
              </Button>
            </li>
          ))}
        </ul>
      </section>
    ) : null;

  const nomenclatorSection = !readOnly ? (
    <section className="border-t border-rule-soft pt-3" ref={containerRef}>
      <p className={sectionLabelCls}>Caută în nomenclator</p>
      <div className="relative">
        <Input
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeIndex >= 0 ? `${listboxId}-opt-${activeIndex}` : undefined}
          aria-label="Caută în nomenclator"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          onKeyDown={handleSearchKeyDown}
          placeholder="Cod MEDOC, cod BD MEDOC sau denumire…"
          className="h-8"
        />
        {/* 2026-08-21 („la cautare in nomenclator, corectia se aplica automat
            — fara confirmare"): the pick has always saved immediately; what
            read as a pending confirmation was the „Salvează"/„Confirmă"
            buttons sitting right under the results. Say so up front, and
            confirm the applied value below once it lands. */}
        <p className="mt-1 text-[12px] text-ink-3">Alegerea din listă se aplică imediat.</p>
        {isFetching && <p className="mt-1 text-[12.5px] text-ink-3">Se caută…</p>}
        {applied && (
          <p role="status" className="mt-1 text-[12.5px] text-ok">
            Aplicat: <span className="font-mono">{applied.codMedoc}</span> — {applied.denumireMedoc}
          </p>
        )}
        {open && results.length > 0 && (
          <ul
            id={listboxId}
            role="listbox"
            className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto border border-rule bg-bg py-1 text-[13.5px] shadow-sm"
          >
            {results.map((entry, i) => (
              <li key={entry.id} id={`${listboxId}-opt-${i}`} role="option" aria-selected={i === activeIndex}>
                <button
                  type="button"
                  onClick={() => pickEntry(entry)}
                  onMouseEnter={() => setActiveIndex(i)}
                  disabled={isPending}
                  className={`block w-full px-3 py-1.5 text-left ${i === activeIndex ? 'bg-wash' : 'hover:bg-wash'}`}
                >
                  <span className="font-mono text-[12.5px] text-ink-2">{entry.codMedoc}</span>
                  {entry.codBdMedoc && (
                    <span className="ml-2 font-mono text-[12.5px] text-ink-3">{entry.codBdMedoc}</span>
                  )}
                  <span className="block truncate text-[13.5px] text-ink">{entry.denumireMedoc}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  ) : null;

  /* B1 (parity — v1 EditModal free-text edit): the escape hatch for a
      correct code the model didn't propose and that isn't in the
      nomenclator. Reuses buildEditPayload (status:'edited' + originals). */
  const manualEditSection = !readOnly ? (
    <section className="border-t border-rule-soft pt-3">
      <p className={sectionLabelCls}>Editează manual</p>
      <div className="space-y-2">
        <div>
          <Input
            aria-label="Cod MEDOC"
            value={editCod}
            onChange={(e) => setEditCod(e.target.value)}
            placeholder="Cod MEDOC"
            className="h-8 font-mono"
            {...fieldAria(!!codErr, `cod-error-${row.id}`)}
          />
          {codErr && <p id={`cod-error-${row.id}`} className="mt-1 text-[12.5px] text-bad">{codErr}</p>}
        </div>
        <div>
          <Input
            aria-label="Denumire MEDOC"
            value={editDen}
            onChange={(e) => setEditDen(e.target.value)}
            placeholder="Denumire MEDOC"
            className="h-8"
            {...fieldAria(!!denErr, `den-error-${row.id}`)}
          />
          {denErr && <p id={`den-error-${row.id}`} className="mt-1 text-[12.5px] text-bad">{denErr}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            aria-label="Cod BD MEDOC"
            value={editBd}
            onChange={(e) => setEditBd(e.target.value)}
            placeholder="Cod BD MEDOC (opțional)"
            className="h-8 w-48 font-mono"
          />
          <Button
            type="button"
            size="sm"
            disabled={isPending || readOnly || !editValid}
            onClick={() =>
              resolve(
                buildEditPayload(row, {
                  codMedoc: editCod.trim(),
                  denumireMedoc: editDen.trim(),
                  codBdMedoc: editBd.trim() || undefined,
                })
              )
            }
          >
            Salvează
          </Button>
        </div>
      </div>
    </section>
  ) : null;

  const lockedNote = readOnly ? (
    <p className="text-[12.5px] text-ink-3">Maparea este blocată pentru editare — redeschideți pentru a edita.</p>
  ) : null;

  /* Fix 5 (review mutations fail silently): `resolveEntry` failures
      (e.g. a 409/403 from the server, via `ApiClientError`) previously
      rendered nothing — the operator's click just silently did not
      persist. Surface the mutation's Romanian error message inline. */
  const errorNote = resolveEntry.isError ? (
    <p className="text-[13.5px] text-bad">{resolveEntry.error?.message}</p>
  ) : null;

  /* Visually hidden — the panel has no visible heading. This is the
      focus-in target on open (see the effect above): a screen reader
      announces which row is under review the moment focus lands here,
      and the operator's next Tab starts inside the decision instead of
      two rows below it. */
  const srHeading = (
    <h3 ref={headingRef} tabIndex={-1} className="sr-only">
      Corectare pentru „{row.name}"
    </h3>
  );

  // ── Layout: review ───────────────────────────────────────────────────────
  if (layout === 'review') {
    // Count only — the sections are rendered explicitly below rather than
    // spread from an array, so React never asks for keys on them.
    const secondaryCount = [alternatesSection, votingSection, nomenclatorSection, manualEditSection].filter(
      Boolean
    ).length;
    return (
      <div id={id} ref={panelRef} onKeyDown={handlePanelKeyDown} className="py-1">
        {srHeading}
        {lockedNote}
        {errorNote}

        {/* Decision first. The old layout buried „Fără corespondent" and
            „Marchează incorect" under motivație + alternates + voting +
            search + manual edit, so acting on a row meant scrolling past
            everything that was only there to inform the decision.
            No row identity repeated here — the „De la partener" column
            immediately below already names the row. */}
        <div className="flex flex-wrap justify-end gap-2">
          {confirmButton}
          {faraCorespondentButton}
          {marcheazaIncorectButton}
        </div>

        {/* Side by side, each side named. Stacked one above the other with
            no labels, the partner's denumire and the model's read as one
            list of near-identical strings. */}
        <div className="mt-3 grid gap-x-6 gap-y-3 border-t border-rule-soft pt-3 sm:grid-cols-2">
          <div className="min-w-0">
            <p className={sectionLabelCls}>De la partener</p>
            <p className="font-mono text-[12.5px] text-ink-3">{row.code || '—'}</p>
            <p className="text-[13.5px] text-ink">{row.name}</p>
            {row.value && <p className="mt-0.5 text-[12.5px] text-ink-3">Valoare: {row.value}</p>}
          </div>
          <div className="min-w-0 sm:border-l sm:border-rule-soft sm:pl-6">
            <p className={sectionLabelCls}>
              Propunere AI
              {typeof row.confidence === 'number' && row.confidence > 0 && (
                <span className="ml-1.5 font-normal normal-case tracking-normal tabular-nums text-ink-3">
                  · {Math.round(row.confidence * 100)}%
                </span>
              )}
            </p>
            <p className="font-mono text-[12.5px] text-ink-3">{primaryCodMedoc || '—'}</p>
            <p className="text-[13.5px] text-ink">{primaryDenumireMedoc || 'Fără denumire'}</p>
            {row.codBdMedoc && <p className="mt-0.5 font-mono text-[12.5px] text-ink-3">cod BD: {row.codBdMedoc}</p>}
          </div>
        </div>

        {(reasoningSection || originalSection) && (
          <div className="mt-3 space-y-3 border-t border-rule-soft pt-3">
            {reasoningSection}
            {originalSection}
          </div>
        )}

        {secondaryCount > 0 && (
          <details className="mt-3 border-t border-rule-soft pt-3">
            <summary className="cursor-pointer list-none text-[13.5px] font-medium text-teal marker:content-['']">
              ▸ Altă variantă — alternative, comparație modele, caută în nomenclator, editează manual
            </summary>
            <div className="mt-3 space-y-4">
              {alternatesSection}
              {votingSection}
              {nomenclatorSection}
              {manualEditSection}
            </div>
          </details>
        )}
      </div>
    );
  }

  // ── Layout: stacked (default — ResultsTable's disclosure) ─────────────────
  return (
    <div id={id} ref={panelRef} onKeyDown={handlePanelKeyDown} className="space-y-4 py-1">
      {srHeading}
      {lockedNote}
      {errorNote}
      {reasoningSection}
      {originalSection}

      <section className="border-t border-rule-soft pt-3">
        <p className={sectionLabelCls}>Sugestia principală</p>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="font-mono text-[13.5px] text-ink">{primaryCodMedoc || '—'}</p>
            <p className="truncate text-[13.5px] text-ink-2">{primaryDenumireMedoc || 'Fără denumire'}</p>
          </div>
          {confirmButton}
        </div>
      </section>

      {alternatesSection}
      {votingSection}
      {nomenclatorSection}
      {manualEditSection}

      <section className="border-t border-rule-soft pt-3">
        {faraCorespondentButton}
        <span className="ml-2">{marcheazaIncorectButton}</span>
      </section>
    </div>
  );
}
